import axios from "axios";

import { toast } from "vue-sonner";

import { defineStore } from "@/lib/tellscale/pinia.ts";

import { useApp } from "@/stores/app";
import { useAuth } from "@/stores/auth";

import type {
    HoneypotData,
    RecommendationPortal,
    RecommendationPortalIdentifiers,
    RecommendationPortalResponse
} from "./types.ts";

/**
 * Recommendation portal store managing portal configuration and loading logic
 */
export const useRecommendationPortal = defineStore('recommendation-portal', () => {
    const app = useApp();
    const auth = useAuth();

    const { t } = useI18n();

    const recommendationPortal = ref<RecommendationPortal>();

    const honeypot = ref<HoneypotData>();

    const isLoaded = computed<boolean>(() => !!recommendationPortal.value);

    const isInactive = computed<boolean>(() => {
        return isLoaded.value && !recommendationPortal.value?.active;
    });

    /**
     * Load portal configuration using URL query parameters (unauthenticated access)
     */
    const loadUsingIdentifiers = async (): Promise<void> => {
        console.info("[Portal] Trying to load the recommendation portal using identifiers");

        const identifiers = getIdentifiersFromQuery();

        if(!identifiers) {
            console.warn('No identifiers found to load the recommendation portal');
            return;
        }

        try {
            const { data } = await axios.post<RecommendationPortalResponse>('/api/recommendation/portal/init', {
                recommendation_portal_id: identifiers.portal,
                link_extension: identifiers.code,
            });

            recommendationPortal.value = data.recommendation_portal;
            honeypot.value = data.honeypot;

            updateQueryParams();

            console.info('[Portal] Recommendation portal loaded using identifiers');
        }
        catch(error) {
            console.error(error);

            toast.error(t('errors.loadPortal'));
        }
    }

    /**
     * Load portal configuration using the authenticated promoter session
     */
    const loadUsingAuthentication = async (): Promise<void> => {
        console.info("[Portal] Trying to load the recommendation portal using the authenticated user");

        if(!auth.isAuthenticated) {
            console.info('[Portal] No authenticated user found to load the recommendation portal');
            return;
        }

        try {
            const { data } = await axios.get<RecommendationPortalResponse>('/api/recommendation/portal/init');

            recommendationPortal.value = data.recommendation_portal;
            honeypot.value = data.honeypot;

            updateQueryParams();

            console.info('[Portal] Recommendation portal loaded using authenticated user');
        }
        catch(error) {
            console.error(error);

            toast.error(t('errors.loadPortal'));
        }
    }

    /**
     * Extract portal and code identifiers from URL query parameters
     *
     * @returns {RecommendationPortalIdentifiers | undefined} Parsed identifiers or undefined if invalid
     */
    const getIdentifiersFromQuery = (): undefined | RecommendationPortalIdentifiers => {
        const query = useQueryParams();

        const portal = query.get('portal');
        const code = query.get('code');

        if(!portal || !code) return;

        try {
            return {
                portal: parseInt(portal),
                code: parseInt(code),
            };
        }
        catch {
            return;
        }
    }

    /**
     * Update URL query parameters with portal id and code using native browser History API
     */
    const updateQueryParams = (): void => {
        if(!recommendationPortal.value) return;

        const url = new URL(window.location.href);

        url.searchParams.set('portal', recommendationPortal.value.id.toString());
        url.searchParams.set('code', recommendationPortal.value.code);

        window.history.replaceState({}, '', url.toString());
    }

    /**
     * Check if a specific widget is enabled
     *
     * Combines two gates:
     *   1. The widget key is NOT listed in TS_DISABLED_FEATURES (operator-controlled,
     *      env-driven via service-tellscale-public)
     *   2. The portal owner has enabled the widget in their portal settings (user-controlled)
     *
     * Returns true by default for configurable widgets (opt-out).
     *
     * @param {string} widgetKey - The widget key to check
     * @returns {boolean} Whether the widget is enabled
     */
    const isWidgetEnabled = (widgetKey: string): boolean => {
        if (!app.isFeatureEnabled(`widget_${widgetKey}`)) return false;

        return recommendationPortal.value?.settings?.widgets?.[widgetKey] ?? true;
    };

    /**
     * Mark the welcome video as seen for the current promoter
     *
     * @param {string} videoId - The video ID to mark as seen
     * @returns {Promise<boolean>} True if successful, false otherwise
     */
    const markWelcomeVideoSeen = async (videoId: string): Promise<boolean> => {
        try {
            await axios.post('/api/recommendation/portal/video/seen', {
                video_id: videoId,
            });

            // Update the local promoter state
            if(auth.promoter) {
                auth.promoter.rp_welcome_video_seen_id = videoId;
            }

            return true;
        }
        catch(error) {
            console.error('Failed to mark welcome video as seen:', error);
            return false;
        }
    }

    /**
     * Reset store to initial state
     */
    const reset = (): void => {
        recommendationPortal.value = undefined;
        honeypot.value = undefined;
    }

    return {
        value: recommendationPortal, honeypot, isLoaded, isInactive,
        getIdentifiersFromQuery, loadUsingIdentifiers, loadUsingAuthentication, isWidgetEnabled, markWelcomeVideoSeen, reset
    };
});